"use client";

import { CompanyStateProvider } from "@offstreetparking/portal-flow/providers/company";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { createContext, useContext, useState } from "react";
import { fetchCompanyBySubdomain } from "./fetchCompanyBySubdomain";

type Company = Awaited<ReturnType<typeof fetchCompanyBySubdomain>>;

type CompanyContextValue = {
	company?: Company;
	isPending: boolean;
	error?: Error;
};

const CompanyContext = createContext<CompanyContextValue | undefined>(undefined);

export const useCompany = () => {
	const context = useContext(CompanyContext);
	if (context === undefined) {
		throw new Error("useCompany must be used within a CompanyProvider");
	}
	return context;
};

type CompanyProviderProps = {
	children: React.ReactNode;
	company: CompanyContextValue["company"];
	locale?: string;
};

const CompanyInnerProvider = ({ children, company: initialData, locale }: CompanyProviderProps) => {
	const {
		data: company,
		isPending,
		error,
	} = useQuery({
		queryKey: ["company.fetchCompanyBySubdomain", locale],
		queryFn: () => fetchCompanyBySubdomain({ locale }),
		initialData,
	});
	return (
		<CompanyContext.Provider value={{ company, isPending, error: error ?? undefined }}>
			<CompanyStateProvider value={{ company, isPending, error: error ?? undefined }}>
				{children}
			</CompanyStateProvider>
		</CompanyContext.Provider>
	);
};

export const CompanyProvider = ({ children, company, locale }: CompanyProviderProps) => {
	const [queryClient] = useState(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						refetchOnWindowFocus: false,
						retry: 2,
					},
				},
			}),
	);
	return (
		<QueryClientProvider client={queryClient}>
			<CompanyInnerProvider company={company} locale={locale}>
				{children}
			</CompanyInnerProvider>
		</QueryClientProvider>
	);
};
